// ===============================================
// Edit Project Page 
// หน้าแก้ไขข้อมูลโครงการ
// ===============================================

import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Row,
  Col,
  Select,
  DatePicker,
  Input,
  InputNumber,
  Button,
  Space,
  Typography,
  Progress,
  Divider,
  message,
  Alert,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  ProjectOutlined,
  CalendarOutlined,
  UserOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext'
import { useProjects, getStatusDisplayName, getPriorityDisplayName } from '../../hooks/useProjects';
import api from '../../utils/api';

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

// ค่าคงที่สำหรับสถานะและความสำคัญ
const PROJECT_STATUS = {
  planning: 'planning',
  active: 'active',
  on_hold: 'on_hold',
  completed: 'completed',
  cancelled: 'cancelled'
};

const PROJECT_PRIORITY = {
  low: 'low',
  medium: 'medium',
  high: 'high',
  urgent: 'urgent'
};

const EditProjectPage = () => {
  const { user, hasPermission } = useAuth();

  const navigate = useNavigate();
  const { project_id } = useParams(); // รับ project_id จาก URL params

  // State management
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [project, setProject] = useState(null);
  const [managerOptions, setManagerOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);

  // Hooks
  const { getProjectById, updateProject } = useProjects();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);

  // Permission checks
  const canCreate = user?.permissions?.projects?.create === true;
  const canUpdate = user?.permissions?.projects?.update === true;
  const canDelete = user?.permissions?.projects?.delete === true;
  const canView = user?.permissions?.projects?.read === true;

  // ดึงข้อมูลผู้จัดการ (users)
  const fetchUsers = async () => {
    console.log('Fetch Users Data...');
    try {
      const response = await api.get('/api/users', {
        params: { role_id: 'SUPER_ADMIN', is_active: 'true' }
      });

      console.log('users Data', response.data);
      if (response.data.success) {
        const managerData = response.data.data.map(user => ({
          value: user.id,
          label: user.name,
          user: user
        }));
        setManagerOptions(managerData);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // ดึงข้อมูลลูกค้า (clients/contacts)
  const fetchClients = async () => {
    try {
      const response = await api.get('/api/contacts', {
        params: { type: 'client', active: 'true' }
      });
      if (response.data.success) {
        const clientData = response.data.data.map(client => ({
          value: client.id,
          label: client.name || client.company_name,
          contact: client
        }));
        setClientOptions(clientData);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  // โหลดข้อมูลโครงการสำหรับแก้ไข
  const fetchProject = async () => {
    setLoading(true);
    try {
      const projectData = await getProjectById(project_id);

      if (!projectData) {
        // Project ไม่พบ - แสดงข้อความผิดพลาด
        message.error('ไม่พบข้อมูลโครงการ');
        return;
      }

      setProject(projectData);

      // ตั้งค่าข้อมูลในฟอร์ม
      form.setFieldsValue({
        ...projectData,
        start_date: projectData.start_date ? dayjs(projectData.start_date) : null,
        end_date: projectData.end_date ? dayjs(projectData.end_date) : null,
      });

    } catch (error) {
      message.error('เกิดข้อผิดพลาดในการโหลดข้อมูลโครงการ');
      console.error('Error fetching project:', error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (!user || !canUpdate || !project_id) return;
    fetchProject();
    fetchUsers();
    fetchClients();
  }, [project_id, canUpdate, user]);

  // บันทึกการแก้ไขโครงการ
  const handleSave = async (status) => {
    try {

      
      console.log('values', values);

      const values = await form.validateFields();


      setSaveLoading(true);

      const projectData = {
        ...values,
        status,
        start_date: values.start_date?.format('YYYY-MM-DD'),
        end_date: values.end_date?.format('YYYY-MM-DD'),
        updated_by: user.id
      };

      // เรียก API update project
      await updateProject(project_id, projectData);

      message.success('อัปเดตข้อมูลโครงการสำเร็จ');
      navigate('/projects');

    } catch (error) {
      if (error?.errorFields) {
        // Form validation error
        return;
      }

      message.error('เกิดข้อผิดพลาดในการอัปเดตข้อมูลโครงการ');
      console.error('Error updating project:', error);
    } finally {
      setSaveLoading(false);
    }
  };

  // คำนวณความก้าวหน้า
  const calculateProgress = () => {
    if (!project) return 0;

    // คำนวณจากวันที่เริ่มและสิ้นสุด
    if (project.start_date && project.end_date) {
      const start = new Date(project.start_date);
      const end = new Date(project.end_date);
      const today = new Date();

      if (today < start) return 0; // ยังไม่เริ่ม
      if (today > end) return 100; // เกินกำหนดแล้ว

      const totalDuration = end - start;
      const elapsed = today - start;

      return Math.min(100, Math.max(0, Math.round((elapsed / totalDuration) * 100)));
    }

    // ใช้ค่าที่บันทึกไว้
    return project.progress_percentage || 0;
  };

  // คอลัมน์ข้อมูลที่แสดงแบบ Read-only
  const readOnlyFields = [
    {
      label: 'วันที่สร้าง',
      value: project?.created_at ? dayjs(project.created_at).format('DD/MM/YYYY HH:mm') : '-',
      icon: <CalendarOutlined />
    },
    {
      label: 'ผู้สร้าง',
      value: project?.created_by_name || '-',
      icon: <UserOutlined />
    },
    {
      label: 'วันที่อัปเดตล่าสุด',
      value: project?.updated_at ? dayjs(project.updated_at).format('DD/MM/YYYY HH:mm') : '-',
      icon: <CalendarOutlined />
    }
  ];

  // ถ้าโหลดข้อมูลแล้วไม่พบโครงการ
  if (loading === false && !project) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Alert
          message="ไม่พบข้อมูลโครงการ"
          description="โครงการที่คุณต้องการแก้ไขไม่พบในระบบ หรือถูกลบไปแล้ว"
          type="error"
          showIcon
        />
        <Button
          type="primary"
          style={{ marginTop: 16 }}
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/projects')}
        >
          กลับไปหน้ารายการโครงการ
        </Button>
      </div>
    );
  }

  // ตรวจสอบ permission สำหรับการแสดง UI
  if (!hasPermission('projects', 'update')) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Alert
          message="ไม่มีสิทธิ์เข้าถึง"
          description="คุณไม่มีสิทธิ์ในการแก้ไขข้อมูลโครงการ"
          type="error"
          showIcon
        />
        <Button
          type="primary"
          style={{ marginTop: 16 }}
          onClick={() => navigate('/projects')}
        >
          กลับไปหน้าโครงการ
        </Button>
      </div>
    );
  }

  return (
    <div className="edit-project-page">
      {/* Header */}
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/projects')}
              >
                กลับ
              </Button>
              <Title level={4} style={{ margin: 0 }}>
                <ProjectOutlined /> แก้ไขข้อมูลโครงการ
              </Title>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                loading={saveLoading}
                onClick={() => handleSave(project?.status || PROJECT_STATUS.planning)}
              >
                บันทึก
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={saveLoading}
                onClick={() => handleSave(PROJECT_STATUS.active)}
              >
                บันทึกและเปิดใช้งาน
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Form
        form={form}
        layout="vertical"
        // onFinish={handleSave}
        disabled={loading || saveLoading}
      >
        <Row gutter={[16, 16]}>
          {/* ข้อมูลพื้นฐาน */}
          <Col xs={24} lg={16}>
            <Card title="ข้อมูลพื้นฐานโครงการ" style={{ marginBottom: 24 }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="ชื่อโครงการ"
                    name="project_name"
                    rules={[
                      { required: true, message: 'กรุณากรอกชื่อโครงการ' },
                      { min: 3, message: 'ชื่อโครงการต้องมีอย่างน้อย 3 ตัวอักษร' }
                    ]}
                  >
                    <Input placeholder="กรอกชื่อโครงการ" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="รหัสโครงการ"
                    name="project_code"
                  >
                    <Input placeholder="กรอกรหัสโครงการ (ไม่บังคับ)" />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    label="รายละเอียด"
                    name="description"
                    rules={[
                      { required: true, message: 'กรุณากรอกรายละเอียดโครงการ' },
                      { min: 10, message: 'รายละเอียดต้องมีอย่างน้อย 10 ตัวอักษร' }
                    ]}
                  >
                    <TextArea
                      rows={4}
                      placeholder="กรอกรายละเอียดของโครงการ"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="หมวดหมู่"
                    name="category"
                  >
                    <Input placeholder="กรอกหมวดหมู่โครงการ" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="ระดับความสำคัญ"
                    name="priority"
                    rules={[{ required: true, message: 'กรุณาเลือกระดับความสำคัญ' }]}
                  >
                    <Select placeholder="เลือกระดับความสำคัญ">
                      {Object.entries(PROJECT_PRIORITY).map(([key, value]) => (
                        <Option key={value} value={value}>
                          {getPriorityDisplayName(value)}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* ข้อมูลการจัดการ */}
            <Card title="ข้อมูลการจัดการ" style={{ marginBottom: 24 }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="ผู้จัดการโครงการ"
                    name="manager_id"
                    rules={[{ required: true, message: 'กรุณาเลือกผู้จัดการโครงการ' }]}
                  >
                    <Select
                      placeholder="เลือกผู้จัดการโครงการ"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children.toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {managerOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="ลูกค้า"
                    name="client_id"
                    rules={[{ required: true, message: 'กรุณาเลือกลูกค้า' }]}
                  >
                    <Select
                      placeholder="เลือกลูกค้า"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children.toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {clientOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="วันที่เริ่มต้น"
                    name="start_date"
                    rules={[{ required: true, message: 'กรุณาเลือกวันที่เริ่มต้น' }]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="วันที่สิ้นสุด"
                    name="end_date"
                    rules={[{ required: true, message: 'กรุณาเลือกวันที่สิ้นสุด' }]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="งบประมาณ (บาท)"
                    name="budget"
                    rules={[
                      { required: true, message: 'กรุณากรอกงบประมาณ' },
                      { type: 'number', min: 0, message: 'งบประมาณต้องเป็นตัวเลขที่ไม่ติดลบ' }
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="0"
                      min={0}
                      formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="สถานะ"
                    name="status"
                    rules={[{ required: true, message: 'กรุณาเลือกสถานะ' }]}
                  >
                    <Select placeholder="เลือกสถานะ">
                      {Object.entries(PROJECT_STATUS).map(([key, value]) => (
                        <Option key={value} value={value}>
                          {getStatusDisplayName(value)}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* ข้อมูลเสริมและสถิติ */}
          <Col xs={24} lg={8}>
            {/* ความก้าวหน้า */}
            <Card title="ความก้าวหน้า" style={{ marginBottom: 24 }}>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <Progress
                  type="circle"
                  percent={calculateProgress()}
                  format={percent => `${percent}%`}
                  width={120}
                />
              </div>
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary">
                  คำนวณจากวันที่เริ่มและสิ้นสุด
                </Text>
              </div>
            </Card>

            {/* ข้อมูล Read-only */}
            <Card title="ข้อมูลการสร้าง">
              {readOnlyFields.map((field, index) => (
                <div key={index} style={{ marginBottom: 12 }}>
                  <Space>
                    {field.icon}
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {field.label}
                      </Text>
                      <div>
                        <Text strong>{field.value}</Text>
                      </div>
                    </div>
                  </Space>
                  {index < readOnlyFields.length - 1 && <Divider style={{ margin: '12px 0' }} />}
                </div>
              ))}
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default EditProjectPage;